import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Edit, FileText, Layers, Link2, Plus, Search, Trash2, Video } from 'lucide-react';
import type { GroupContentType, ListGroupContentsQuery } from '@news-cms/shared';
import { Button, buttonVariants } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { SuperAdminFallback } from '@/components/auth/super-admin-fallback';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import { groupContentsListQueryOptions, useDeleteGroupContent } from '@/lib/group-contents';
import type { GroupContentSummary } from '@news-cms/shared';

const TYPES: { value: GroupContentType; label: string; icon: typeof Layers }[] = [
  { value: 'article', label: 'บทความ', icon: FileText },
  { value: 'video', label: 'วิดีโอ', icon: Video },
  { value: 'url', label: 'URL', icon: Link2 },
];

interface GroupContentsSearch {
  q?: string;
  type?: GroupContentType;
  page?: number;
}

export const Route = createFileRoute('/_authenticated/group-contents/')({
  validateSearch: (search: Record<string, unknown>): GroupContentsSearch => ({
    q: typeof search.q === 'string' ? search.q : undefined,
    type:
      search.type === 'article' || search.type === 'video' || search.type === 'url'
        ? search.type
        : undefined,
    page: typeof search.page === 'number' ? search.page : undefined,
  }),
  component: GroupContentsPage,
});

function GroupContentsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useSession();
  const canManage = hasPermission(user, 'settings.update');

  const filter: Partial<ListGroupContentsQuery> = {
    q: search.q,
    type: search.type,
    page: search.page ?? 1,
    pageSize: 20,
  };
  const query = useQuery(groupContentsListQueryOptions(filter));
  const deleteMutation = useDeleteGroupContent();
  const [searchInput, setSearchInput] = useState(search.q ?? '');
  const [pendingDelete, setPendingDelete] = useState<GroupContentSummary | null>(null);

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));
  const currentPage = filter.page ?? 1;

  const setType = (type: GroupContentType | undefined) => {
    void navigate({ search: { ...search, type, page: 1 } });
  };
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void navigate({ search: { ...search, q: searchInput.trim() || undefined, page: 1 } });
  };
  const goPage = (page: number) => {
    void navigate({ search: { ...search, page } });
  };

  if (!user || user.role.slug !== 'super_admin') return <SuperAdminFallback />;

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              Group Content
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              กลุ่ม content ที่นำกลับมาใช้ซ้ำได้ — ตั้งครั้งเดียว แล้ว assign เข้า slot ใน /homepage
              (และหน้าอื่นในอนาคต)
            </p>
          </div>
          {canManage && (
            <Link to="/group-contents/new" className={buttonVariants({ variant: 'default' })}>
              <Plus className="mr-1 h-4 w-4" /> สร้าง Group Content
            </Link>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <form onSubmit={handleSearch} className="flex min-w-[200px] flex-1 items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="ค้นหา name หรือ slug…"
                className="pl-8"
              />
            </div>
            <Button type="submit" variant="secondary">
              ค้นหา
            </Button>
          </form>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setType(undefined)}
              className={`rounded-full border px-3 py-1 text-xs ${
                !search.type
                  ? 'border-slate-900 bg-slate-900 text-white dark:border-slate-50 dark:bg-slate-50 dark:text-slate-900'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'
              }`}
            >
              ทุก type
            </button>
            {TYPES.map((t) => {
              const Icon = t.icon;
              const active = search.type === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setType(t.value)}
                  className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs ${
                    active
                      ? 'border-slate-900 bg-slate-900 text-white dark:border-slate-50 dark:bg-slate-50 dark:text-slate-900'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'
                  }`}
                >
                  <Icon className="h-3 w-3" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-4 py-2 font-medium">ชื่อ / slug</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Mode</th>
                <th className="px-4 py-2 text-right font-medium">Items / Max</th>
                <th className="px-4 py-2 font-medium">อัปเดต</th>
                <th className="px-4 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
              {query.isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    กำลังโหลด…
                  </td>
                </tr>
              )}
              {!query.isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    {search.q || search.type
                      ? 'ไม่พบ Group Content ที่ตรง filter'
                      : 'ยังไม่มี Group Content — กดปุ่ม "สร้าง" ด้านบนเพื่อเริ่ม'}
                  </td>
                </tr>
              )}
              {items.map((item) => {
                const typeMeta = TYPES.find((t) => t.value === item.type);
                const Icon = typeMeta?.icon ?? Layers;
                return (
                  <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 dark:text-slate-50">
                        {item.name}
                      </div>
                      <code className="text-xs text-slate-400">{item.slug}</code>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        <Icon className="h-3 w-3" /> {typeMeta?.label ?? item.type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs ${
                          item.mode === 'auto'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                        }`}
                      >
                        {item.mode === 'auto' ? 'อัตโนมัติ' : 'เลือกเอง'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600 dark:text-slate-300">
                      {item.itemCount} / {item.maxCount}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {new Date(item.updatedAt).toLocaleString('th-TH', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to="/group-contents/$id/edit"
                          params={{ id: item.id }}
                          className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                          title="แก้ไข"
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        {canManage && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPendingDelete(item)}
                            title="ลบ"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm">
            <div className="text-slate-500">
              {total} รายการ — หน้า {currentPage}/{totalPages}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => goPage(currentPage - 1)}
              >
                ก่อนหน้า
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => goPage(currentPage + 1)}
              >
                ถัดไป
              </Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="ลบ Group Content?"
        description={
          pendingDelete
            ? `จะลบ "${pendingDelete.name}" — ถ้ายังมี slot ใน /homepage อ้างถึง จะลบไม่ได้ (ต้องเอาออกก่อน)`
            : ''
        }
        confirmLabel="ลบ"
        variant="destructive"
        isLoading={deleteMutation.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          deleteMutation.mutate(pendingDelete.id, {
            onSuccess: () => setPendingDelete(null),
          });
        }}
      />
    </div>
  );
}
